import { defineAsyncComponent } from 'vue';
import SignUpLoading from '@monolith/legacy/components/sign-up/sign-up-loading.vue';
import SignUpError from '@monolith/legacy/components/sign-up/sign-up-error.vue';

/**
 * Retry budget for the sign-up chunk, exported so it can be asserted directly.
 *
 * It only ever runs when the dynamic import rejects, and the host specs mock sign-up.vue to
 * resolve -- a mock that rejected would also break the static `import SignUp` they assert
 * against. So the budget is verified as a plain function rather than through a rigged loader.
 *
 * `_error` is deliberately unread: fail() hands it back to Vue, which routes it through
 * handleError to the app-level errorHandler that @sentry/vue installs.
 */
export function retrySignUpChunkOnce(
  _error: Error,
  retry: () => void,
  fail: () => void,
  attempts: number
): void {
  // `attempts` arrives as retries + 1, so this is exactly one retry.
  if (attempts <= 1) {
    retry();
    return;
  }
  fail();
}

/**
 * The retailer sign-up wizard, loaded on demand.
 *
 * Async because auth-popin is globally registered (core/plugins/global-components.ts) and its
 * wrapper mounts on layouts/default.blade.php: a static import there put the entire wizard in
 * the main bundle of every page. That was also the last static edge into sign-up.vue, so
 * cutting it made register-page.vue's lazy import a real network fetch rather than a resolve
 * out of an already-loaded bundle.
 *
 * Shared rather than owned by either host for that same reason. Both auth-popin and
 * register-page render this wizard, and the second one only started being able to fail once
 * the first one stopped pulling it in eagerly -- so the loading and failure states belong with
 * the loader, not in whichever host happened to be the one that made it async.
 *
 * Module scope is load-bearing: defineAsyncComponent closes over a `retries` counter that Vue
 * increments and never resets, so declaring the handle here gives one retry budget per page
 * load, shared by every host. Built inside a `setup()` it would be one budget per popin open,
 * and a chunk that stays broken would burn a fresh pair of requests on every Register click.
 */
export const AsyncSignUp = defineAsyncComponent({
  loader: () => import('@monolith/legacy/components/sign-up/sign-up.vue'),
  loadingComponent: SignUpLoading,
  // Vue fully handles the loader rejection (it settles the promise itself and routes the error
  // through handleError), so it never surfaces as an unhandledrejection and
  // core/utilities/chunk-load-error-handler.ts structurally cannot see it. Its reload recovery
  // covers a host's own route chunk via router.onError but not this nested one, which is why
  // the error state offers the reload itself instead of relying on that handler.
  errorComponent: SignUpError,
  // Vue never times out by default, and a request that never settles reaches neither onError nor
  // errorComponent: a captive portal or a hung socket would leave the host waiting forever.
  timeout: 30000,
  // The error itself is deliberately unread: fail() hands it back to Vue, which reports it via
  // the app-level errorHandler that @sentry/vue installs, so there is nothing to re-surface.
  onError: retrySignUpChunkOnce,
});

// defineAsyncComponent names its wrapper `AsyncComponentWrapper`, which is what surfaces in Vue's
// warning traces and devtools, and -- because an imported handle never lands in a host's
// setupState the way a local binding does -- it is also the only name @vue/test-utils can infer
// for it. So an unnamed handle makes the stub key depend on how each host happens to bind it.
//
// Deliberately not `SignUp`: that is the inner component's own name, and VTU falls back to name
// matching for `findComponent(SignUp)`, which would then stop at this wrapper instead of the
// wizard it resolves to. Hosts opt the wrapper out of shallowMount with
// `stubs: { AsyncSignUp: false }`.
Object.defineProperty(AsyncSignUp, 'name', {
  value: 'AsyncSignUp',
  writable: true,
  enumerable: true,
  configurable: true,
});
